import { useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { global } from "@/global";
import { LocationOccupancyState } from "@/resources/locationOccupancy";
import { Location } from "@/resources/types";

const getErrorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
  ?? (err as Error)?.message
  ?? fallback;

type OccupancyRow = {
  id: string;
  name: string;
  count: number;
};

const buildOccupancyRows = (counts: Record<string, number>, locations: Location[]) => {
  const nameById = Object.fromEntries(locations.map((location) => [location.id, location.name]));
  return Object.entries(counts)
    .map(([id, count]): OccupancyRow => ({
      id,
      name: nameById[id] ?? id,
      count,
    }))
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.name.localeCompare(b.name);
    });
};

type LocationOccupancyAdminActionsProps = {
  locations: Location[];
};

const OccupancyViewDialog = ({
  open,
  loading,
  error,
  rows,
  onClose,
}: {
  open: boolean;
  loading: boolean;
  error: string | null;
  rows: OccupancyRow[];
  onClose: () => void;
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>Location occupancy state</DialogTitle>
    <DialogContent>
      <Stack spacing={2} sx={{ mt: 1 }}>
        {loading && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={18} />
            <span>Loading occupancy state...</span>
          </Stack>
        )}
        {error && (
          <Alert severity="error">{error}</Alert>
        )}
        {!loading && !error && rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">No occupied locations.</Typography>
        )}
        {!loading && !error && rows.length > 0 && (
          <TableContainer sx={{ maxHeight: 480 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Location</TableCell>
                  <TableCell align="right" width={100}>Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell align="right">{row.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);

export const LocationOccupancyAdminActions = ({ locations }: LocationOccupancyAdminActionsProps) => {
  const [rebuilding, setRebuilding] = useState(false);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: "success" | "error" } | null>(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [occupancyRows, setOccupancyRows] = useState<OccupancyRow[]>([]);
  const [viewError, setViewError] = useState<string | null>(null);

  const handleRebuild = async () => {
    setRebuilding(true);
    try {
      const res = await global.client.post("meta/custom/location-occupancy", {});
      const { locationCount = 0, durationMs = 0 } = (res.data ?? {}) as { locationCount?: number; durationMs?: number };
      setSnackbar({
        severity: "success",
        message: `Rebuilt occupancy for ${locationCount} location${locationCount === 1 ? "" : "s"} (${durationMs}ms)`,
      });
    } catch (err: unknown) {
      setSnackbar({
        severity: "error",
        message: getErrorMessage(err, "Failed to rebuild location occupancy"),
      });
    } finally {
      setRebuilding(false);
    }
  };

  const handleView = async () => {
    setViewOpen(true);
    setViewLoading(true);
    setViewError(null);
    setOccupancyRows([]);
    try {
      const res = await global.client.get("meta/custom/location-occupancy");
      const { counts = {} } = (res.data ?? {}) as LocationOccupancyState;
      setOccupancyRows(buildOccupancyRows(counts, locations));
    } catch (err: unknown) {
      setViewError(getErrorMessage(err, "Failed to load location occupancy"));
    } finally {
      setViewLoading(false);
    }
  };

  const handleCloseView = () => {
    setViewOpen(false);
    setOccupancyRows([]);
    setViewError(null);
  };

  return (
    <>
      <Button variant="outlined" onClick={handleRebuild} disabled={rebuilding}>
        {rebuilding ? "Rebuilding..." : "Rebuild occupancy"}
      </Button>
      <Button variant="outlined" onClick={handleView} disabled={viewLoading && !viewOpen}>
        View occupancy
      </Button>

      <Snackbar open={snackbar !== null} autoHideDuration={6000} onClose={() => setSnackbar(null)}>
        <Alert severity={snackbar?.severity ?? "info"} onClose={() => setSnackbar(null)} sx={{ width: "100%" }}>
          {snackbar?.message}
        </Alert>
      </Snackbar>

      <OccupancyViewDialog
        open={viewOpen}
        loading={viewLoading}
        error={viewError}
        rows={occupancyRows}
        onClose={handleCloseView}
      />
    </>
  );
};
